import {useContext, useEffect, useState} from 'react'
import {Col, Flex, Form, Modal, Row, Typography} from 'antd'
import {useIntl} from 'react-intl'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import useDebouncedValue from '../../../../utils/common_function'
import {SET_SHOW_TEST_ZONE_POPUP, TestZoneContext} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import TextArea from '../../../common/TextArea'
import Button from '../../../common/Button'

const TestZoneFormModal = (props) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(TestZoneContext)
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()

  const [plantOptions, setPlantOptions] = useState([])
  const [plantSearchFailed, setPlantSearchFailed] = useState(false)
  const [plantSearch, setPlantSearch] = useState('')
  const [plantLoading, setPlantLoading] = useState(false)
  const debouncedPlantSearch = useDebouncedValue(plantSearch, 500)

  const selected = state?.selected_test_zone || {}

  const closeFormPopup = () => {
    dispatch({ type: SET_SHOW_TEST_ZONE_POPUP, data: false })
    form.resetFields()
  }

  const getInitialValues = () => ({
    is_create: !selected?.id,
    id: selected?.id || undefined,
    plant: selected?.plant
      ? { value: selected.plant.id, label: selected.plant.name }
      : undefined,
    specifier: selected?.specifier || '',
    description: selected?.description || '',
  })

  const fetchPlants = async (searchTerm = '') => {
    setPlantLoading(true)
    try {
      let res = await getRequest(`/api/plants/?search=${searchTerm}`)
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setPlantSearchFailed(true)
        setPlantOptions([])
        return
      }
      setPlantSearchFailed(false)
      const results = res?.results || []
      setPlantOptions(
        results.map((item) => ({ value: item.id, label: item.name }))
      )
    } finally {
      setPlantLoading(false)
    }
  }

  useEffect(() => {
    if (!state.show_test_zone_popup) return
    fetchPlants('')
  }, [state.show_test_zone_popup])

  useEffect(() => {
    if (debouncedPlantSearch !== undefined) {
      fetchPlants(debouncedPlantSearch)
    }
  }, [debouncedPlantSearch])

  useEffect(() => {
    if (!state.show_test_zone_popup) return
    form.setFieldsValue(getInitialValues())
  }, [state.show_test_zone_popup])

  const saveTestZone = async (values) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'testZones.save' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    let res = null
    if (selected?.id) {
      res = await putRequest(`/api/test_zone/${selected.id}/`, formData)
    } else {
      res = await postRequest(`/api/test_zone/`, formData)
    }

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: selected?.id
              ? 'testZones.updateSuccess'
              : 'testZones.saveSuccess',
          }),
        },
      })
      props.getTestZoneListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'testZones.saveError' }),
        },
      })
    }
  }

  return (
    <Modal
      open={state.show_test_zone_popup}
      title={
        selected?.id
          ? intl.formatMessage({ id: 'testZones.updateTitle' })
          : intl.formatMessage({ id: 'testZones.createTitle' })
      }
      width="50%"
      className="test-zones-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="test-zone-form"
          name="test-zone-form"
          form={form}
          layout="vertical"
          onFinish={saveTestZone}
        >
          <InputText name="is_create" hidden={true} isRequired={false} />
          <InputText name="id" hidden={true} isRequired={false} />
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <SelectInput
                name="plant"
                label={intl.formatMessage({ id: 'testZones.selectPlant' })}
                options={plantOptions}
                placeholder={intl.formatMessage({
                  id: 'testZones.selectPlant',
                })}
                isRequired={true}
                allowClear={true}
                showSearch={true}
                showLoader={plantLoading}
                onInputChange={(value) => setPlantSearch(value || '')}
                notFoundContent={
                  plantSearchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
              />
            </Col>
            <Col span={24}>
              <InputText
                name="specifier"
                label={intl.formatMessage({ id: 'testZones.specifier' })}
                isRequired={true}
              />
            </Col>
            <Col span={24}>
              <TextArea
                name="description"
                label={intl.formatMessage({ id: 'testZones.description' })}
                isRequired={false}
              />
            </Col>
            <Col span={24}>
              <Flex justify="end" className="py-4">
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'testZones.save' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default TestZoneFormModal
